import React from 'react';

import {assert} from 'Asserts';
import {TargetFile} from 'folder-items/FileConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {formatTypedValue, parseTextAsFloat} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  ProcedureValidationError,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  buildTooltipFromErrors,
  findRelevantProcedureValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {
  DataParameterTypedValue,
  EquipmentTypedValue,
  MaterialInputTypedValue,
  ProcedureDataParameterSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterFieldType,
  EquipmentType,
  MaterialType,
  UnitSummary,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Optional} from 'util/Types';

import {
  CONFIRMATION_METHOD_TO_USER_FACING_TEXT,
  USER_FACING_TEXT_TO_CONFIRMATION_METHOD,
} from './ProcedureTableConstants';

export const cellText = (cell: Cell): string =>
  cell.text || (cell.typedValue ? formatTypedValue(cell.typedValue) : '');

export const cellMinMaxTypedValue = (
  cell: Cell,
  parameterType: FieldType
): FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null => {
  if (parameterType === FieldType.FLOAT) {
    // typedValue might not be a float if the type selector just changed.
    // It appears to change to a StringValueType whenever we toggle from Text to Decimal, though it is a
    // FloatTypedValue the rest of the time (which is a proper TypedValue).
    //
    // After entering text the first time, the type is empty and all we have is the cell text.
    if (cell.typedValue?.type !== FieldType.FLOAT) {
      return {
        type: FieldType.FLOAT,
        value: cell.text,
        rawValue: cell.text,
        floatValue: null,
      };
    }
    return cell.typedValue;
  } else if (parameterType === FieldType.DATETIME) {
    if (cell.typedValue?.type !== FieldType.DATETIME) {
      return {
        type: FieldType.DATETIME,
        value: cell.text,
        datetimeValue: cell.text,
      };
    }
    return cell.typedValue;
  }
  return null;
};

export const cellFloat = (cell: Cell): number | null => (cell.text ? parseTextAsFloat(cell.text) : null);

export function makeSetpointCell(
  dataParameter: ProcedureDataParameterSetting,
  options: {error?: Optional<EvaluatedCellError>} = {
    error: null,
  }
): Cell {
  const {error} = options;
  if (dataParameter.values.length === 0) {
    switch (dataParameter.dataParameterVersion.dataType.type) {
      case FieldType.TEXT:
        return {
          text: '',
          typedValue: {
            type: FieldType.TEXT,
            value: null,
          },
          error,
        };
      case FieldType.DATETIME:
        return {
          text: '',
          typedValue: {
            type: FieldType.DATETIME,
            value: null,
            datetimeValue: null,
          },
          error,
        };
      case FieldType.FLOAT:
        return {
          text: '',
          typedValue: {
            type: FieldType.FLOAT,
            value: null,
            rawValue: null,
            floatValue: null,
          },
          error,
        };
      default:
        assert(false, 'Unsupported data type for setpoint cell', {
          dataType: dataParameter.dataParameterVersion.dataType,
        });
    }
  }
  return {
    text: String(dataParameter.values[0].value.value ?? ''),
    typedValue: dataParameter.values[0].value,
    error,
  };
}

export function doesFieldTypeSupportMinMax(type: FieldType): boolean {
  return type === FieldType.FLOAT || type === FieldType.DATETIME;
}

export function makeDataParameterTypedValueFromText(
  targetType: DataParameterFieldType,
  textValue: string | null
): DataParameterTypedValue {
  switch (targetType) {
    case FieldType.TEXT:
      return {type: FieldType.TEXT, value: textValue};
    case FieldType.FLOAT:
      return {type: FieldType.FLOAT, value: textValue, rawValue: textValue, floatValue: null};
    case FieldType.DATETIME:
      return {type: FieldType.DATETIME, value: textValue, datetimeValue: null};
    default:
      assert(false, 'Unknown data parameter field type', {targetType});
  }
}

export function makeMinMaxCell(
  dataParameter: ProcedureDataParameterSetting,
  value?: FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null,
  options: {error?: Optional<EvaluatedCellError>} = {
    error: null,
  }
): Cell {
  const {error} = options;
  const dataType = dataParameter.dataParameterVersion.dataType;
  if (!doesFieldTypeSupportMinMax(dataType.type)) {
    return makeStringCell('', {
      cellLockedMessage: 'Minimum and Maximum can only be set for Decimal and Datetime parameters',
    });
  }

  if (dataType.type === FieldType.DATETIME) {
    return {
      text: value?.value ?? '',
      typedValue: value ?? {
        type: FieldType.DATETIME,
        value: null,
        datetimeValue: null,
      },
      error,
    };
  }

  return {
    text: value?.value ?? '',
    typedValue: value ?? {
      type: FieldType.FLOAT,
      value: '',
      rawValue: '',
      floatValue: null,
    },
    error,
  };
}

export function getUserAnnotationIndicator(message?: string | null): JSX.Element | null {
  return message ? (
    <ErrorTriangle
      state={TriangleState.VALID_WITH_TRIANGLE}
      error={{type: '', message}}
      location={TriangleLocation.CELL}
    />
  ) : null;
}

export function makeStringCell(
  text: string | number | null | undefined,
  options: {cellLockedMessage?: Optional<string>; error?: Optional<EvaluatedCellError>} = {
    cellLockedMessage: null,
    error: null,
  }
): Cell {
  const str = String(text ?? '');
  const {cellLockedMessage, error} = options;
  const userAnnotationIndicator = getUserAnnotationIndicator(cellLockedMessage);

  return {
    text: str,
    typedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: str,
    },
    locked: cellLockedMessage != null,
    userAnnotationIndicator,
    error,
  };
}

export function makeJsonCell<JsonTypedValueType>(
  jsonValue: JsonTypedValueType,
  options: {cellLockedMessage?: Optional<string>; error?: Optional<EvaluatedCellError>} = {
    cellLockedMessage: null,
    error: null,
  }
): Cell {
  const {cellLockedMessage, error} = options;
  const userAnnotationIndicator = getUserAnnotationIndicator(cellLockedMessage);

  return {
    text: '',
    typedValue: {type: BaseTypedValueType.JSON, jsonValue},
    locked: cellLockedMessage != null,
    userAnnotationIndicator,
    error,
  };
}

export function makeTextSchemaFieldCell(
  text: string | number | null | undefined,
  options: {cellLockedMessage?: Optional<string>; error?: Optional<EvaluatedCellError>} = {
    cellLockedMessage: null,
    error: null,
  }
): Cell {
  const {cellLockedMessage, error} = options;
  const userAnnotationIndicator = getUserAnnotationIndicator(cellLockedMessage);

  return {
    text: text != null ? String(text) : '',
    typedValue: {
      type: FieldType.TEXT,
      value: text != null ? String(text) : null,
    },
    locked: cellLockedMessage != null,
    userAnnotationIndicator,
    error,
  };
}

export function makeUnitReferenceCell(dataParameter: ProcedureDataParameterSetting): Cell {
  const dataType = dataParameter.dataParameterVersion.dataType;
  if (dataType.type !== FieldType.FLOAT) {
    return makeStringCell('', {
      cellLockedMessage: 'Units can only be set for Decimal parameters',
    });
  }

  return makeUnitReferenceCellFromUnit(dataType.unit);
}

export function makeUnitReferenceCellFromUnit(unit: UnitSummary | null): Cell {
  if (unit == null) {
    return makeStringCell('');
  }
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.UNIT_REFERENCE,
      unitSummary: unit,
    },
  };
}

export function makeEntityLinkCell(
  targetFiles: Array<TargetFile>,
  options: {cellLockedMessage?: Optional<string>; error?: Optional<EvaluatedCellError>} = {
    cellLockedMessage: null,
    error: null,
  }
): Cell {
  const {cellLockedMessage, error} = options;
  const userAnnotationIndicator = getUserAnnotationIndicator(cellLockedMessage);

  return {
    text: '',
    typedValue: {
      type: FieldType.BIOENTITY_LINK,
      targetFiles,
      value: targetFiles.map((t) => t.name).join(', '),
    },
    locked: cellLockedMessage != null,
    userAnnotationIndicator,
    error,
  };
}

export function getConfirmationMethodStructuredTableColumn(): StructuredTableColumn {
  return {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.OPTION_SELECTOR,
      name: 'Confirmation method',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    columnType: TableColumnType.OPTION_SELECTOR, // need to re-specify to narrow types
    options: Object.values(ProcedureParameterConfirmationMethod).map(
      (v) => CONFIRMATION_METHOD_TO_USER_FACING_TEXT[v]
    ),
  };
}

export function makeConfirmationMethodCell(
  value: ProcedureParameterConfirmationMethod,
  error?: Optional<EvaluatedCellError>
): Cell {
  return makeStringCell(CONFIRMATION_METHOD_TO_USER_FACING_TEXT[value], {error});
}

export function getConfirmationMethodValueFromCell(cell: Cell): ProcedureParameterConfirmationMethod {
  const cellTextValue = cellText(cell);
  // It's possible for cell text to initially be empty if the user pastes into the table to create new rows
  // In that case, we default to TABLE
  return USER_FACING_TEXT_TO_CONFIRMATION_METHOD[cellTextValue] ?? ProcedureParameterConfirmationMethod.TABLE;
}

export function extractMaterialFromTypedValue(typedValue?: TypedValue | null): TargetFile | null {
  if (typedValue == null) {
    return null;
  }
  if (typedValue.type === FieldType.BIOENTITY_LINK) {
    const targetFile = typedValue.targetFiles[0];
    if (targetFile?.id != null) {
      return {
        id: targetFile.id,
        name: targetFile.name,
        url: targetFile.url,
      };
    }
  } else if (typedValue.type === BaseTypedValueType.BIOENTITY) {
    return {
      id: typedValue.bioentitySummary.id,
      name: typedValue.bioentitySummary.name,
      url: '',
    };
  }
  return null;
}

/**
 * Extract validation errors for a given method component and convert into a cell error
 */
export function validationErrorsToEvaluatedCellError(
  methodComponentId: string,
  methodComponentType: ProcedureValidationObjectType,
  validationErrors: Array<ProcedureValidationError>
): Optional<EvaluatedCellError> {
  const message = buildTooltipFromErrors(
    findRelevantProcedureValidationErrors(validationErrors, {
      id: methodComponentId,
      objectType: methodComponentType,
    })
  );

  return message != null ? {type: '', message} : null;
}

export function inferPlannedTextOrBioentityLinkTypedValue<
  TType extends MaterialType | EquipmentType,
  TTypedValue extends TType extends MaterialType ? MaterialInputTypedValue : EquipmentTypedValue,
>(dataType: TType, entityCell: Cell): TTypedValue {
  const entityCellType = entityCell.typedValue?.type;
  const selectedType = dataType.type;

  let inferredTypedValue;
  if (entityCellType == null) {
    inferredTypedValue =
      selectedType === FieldType.TEXT
        ? {type: FieldType.TEXT, value: entityCell.text}
        : {
            type: FieldType.BIOENTITY_LINK,
            value: entityCell.text,
            targetFiles: [],
          };
  } else if (selectedType === entityCellType) {
    inferredTypedValue = entityCell.typedValue!;
  } else if (selectedType === FieldType.TEXT) {
    inferredTypedValue = {
      type: FieldType.TEXT,
      value: formatTypedValue(entityCell.typedValue!),
    };
  } else {
    inferredTypedValue = {
      type: FieldType.BIOENTITY_LINK,
      value: cellText(entityCell),
      targetFiles: [],
    };
  }
  return inferredTypedValue as TTypedValue;
}
